/**
 * MarketingLayout - Shared layout wrapper for all pre-login pages
 * Provides consistent navigation, footer, and container structure
 */

import { ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Logo } from '@/components/ui/Logo';
import { PublicFooter } from '@/components/layout/PublicFooter';
import { redirectToStripeCheckout } from '@/lib/stripe';
import { cn } from '@/lib/utils';

interface MarketingLayoutProps {
  children: ReactNode;
  showBackLink?: boolean;
}

const navLinks = [
  { path: '/product', label: 'Product' },
  { path: '/how-it-works', label: 'How It Works' },
  { path: '/pricing', label: 'Pricing' },
  { path: '/use-cases', label: 'Use Cases' },
  { path: '/why', label: 'Why Us' },
];

export function MarketingLayout({ children, showBackLink = false }: MarketingLayoutProps) {
  const location = useLocation();
  const currentPath = location.pathname;

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation - Aligned with App Layout */}
      <nav className="fixed top-0 left-0 right-0 z-50 border-b border-border/40 bg-card/95 backdrop-blur-sm transition-all duration-300">
        <div className="container mx-auto px-4 md:px-6 h-12 flex items-center justify-between max-w-[var(--landing-max-width)]">
          <div className="flex items-center gap-4 md:gap-8">
            <Link to="/">
              <Logo size="sm" showText={false} />
            </Link>
            <nav className="hidden md:flex items-center gap-6 text-sm">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={cn(
                    'transition-colors duration-[var(--duration-standard)] ease-[var(--ease-hover)]',
                    currentPath === link.path
                      ? 'text-foreground font-medium'
                      : 'text-muted-foreground hover:text-foreground'
                  )}
                >
                  {link.label}
                </Link>
              ))}
            </nav>
          </div>
          <div className="flex items-center gap-2 md:gap-3">
            <Button variant="ghost" size="sm" asChild className="text-muted-foreground hover:text-foreground h-8">
              <Link to="/auth">Login</Link>
            </Button>
            <Button 
              variant="default"
              size="sm"
              onClick={redirectToStripeCheckout}
              className="h-8"
            >
              Start Free Trial
            </Button>
          </div>
        </div>
      </nav>

      {/* Main Content — scroll-mt-24 so section titles aren't hidden under fixed header */}
      <main className="pt-12 scroll-mt-24">
        {showBackLink && (
          <div className="container mx-auto px-4 pt-6 max-w-[var(--landing-max-width)]">
            <Link 
              to="/" 
              className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to Home
            </Link>
          </div>
        )}
        {children}
      </main>

      <PublicFooter className="mt-20" />
    </div>
  );
}

